import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Autocomplete, Badge, Button, Card, Center, Divider, FileInput, Group, Loader, Modal, Select, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconUpload } from '@tabler/icons-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Job {
  id: number;
  title: string;
  short_description?: string;
  company_name: string;
  location: string;
  job_type: string;
  experience_range: string;
  is_remote: boolean;
  description: string;
  requirements?: string;
  requires_at_title?: boolean;
  requires_at_diploma_uba?: boolean;
  requires_graduated?: boolean;
  requires_school_inclusion_course?: boolean;
  requires_hr_courses?: boolean;
  careers_affine?: string[];
  posted_by?: number;
  is_approved?: boolean;
  is_active?: boolean;
}

interface Applicant {
  id: number;
  applicant_username: string;
  cover_letter: string;
  resume_url?: string;
  status: string;
  applied_at: string;
  is_withdrawn?: boolean;
  modified_by_applicant?: boolean;
  career?: string;
  career_progress?: string;
}

const JobDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [resume, setResume] = useState<File | null>(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [career, setCareer] = useState('');
  const [careerProgress, setCareerProgress] = useState<string | null>(null);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [showApplicantsList, setShowApplicantsList] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [hasWithdrawn, setHasWithdrawn] = useState(false);
  const [reapplyBlocked, setReapplyBlocked] = useState(false);
  const [updatingJobStatus, setUpdatingJobStatus] = useState(false);
  const [applySuccessMessage, setApplySuccessMessage] = useState<string | null>(null);
  const [approvingJob, setApprovingJob] = useState(false);

  const loadJob = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getJob(Number(id));
      setJob(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadApplicants = useCallback(async () => {
    if (!id) return;
    try {
      setApplicantsLoading(true);
      const response = await api.getJobApplications(Number(id));
      const appData = response;
      setApplicants(appData);
    } catch (err) {
      console.error('Failed to load applicants:', err);
    } finally {
      setApplicantsLoading(false);
    }
  }, [id]);

  const checkIfApplied = useCallback(async () => {
    try {
      const response = await api.getMyApplications();
      const myApps = response;
      const found = myApps.find((app: any) => app.job === Number(id));
      if (!found) {
        setHasApplied(false);
        setHasWithdrawn(false);
        setReapplyBlocked(false);
        return;
      }
      setHasApplied(!found.is_withdrawn);
      setHasWithdrawn(Boolean(found.is_withdrawn));
      setReapplyBlocked((found.withdraw_count || 0) >= 2);
    } catch (err) {
      console.error('Failed to check applications:', err);
    }
  }, [id]);

  const canViewApplicants = useCallback(() => {
    if (!job || !user) return false;
    const isJobOwner = job.posted_by === user.id;
    const isAdminOrMod = ['ADMIN', 'MODERATOR'].includes(user.user_type);
    return isJobOwner || isAdminOrMod;
  }, [job, user]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  useEffect(() => {
    if (job && canViewApplicants()) {
      loadApplicants();
    }
    if (job && user?.user_type === 'JOB_SEEKER') {
      checkIfApplied();
    }
  }, [job, user, canViewApplicants, loadApplicants, checkIfApplied]);

  useEffect(() => {
    if (!applyModalOpen) {
      setCareer('');
      setCareerProgress(null);
    }
  }, [applyModalOpen]);

  const handleApply = async () => {
    if (!id || !resume) return;

    try {
      setApplying(true);
      const formData = new FormData();
      formData.append('resume', resume);
      formData.append('cover_letter', coverLetter);
      if (career) {
        formData.append('career', career);
      }
      if (careerProgress) {
        formData.append('career_progress', careerProgress);
      }

      await api.applyToJob(Number(id), formData);
      setApplyModalOpen(false);
      setHasApplied(true);
      setHasWithdrawn(false);
      setReapplyBlocked(false);
      setResume(null);
      setCoverLetter('');
      setCareer('');
      setCareerProgress(null);
      setApplySuccessMessage(t('applications.applySuccess'));
      // Optionally refresh or redirect
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const canEdit = Boolean(job && user && (job.posted_by === user.id || user.user_type === 'MODERATOR'));
  const canApply = user?.user_type === 'JOB_SEEKER' && !hasApplied && !reapplyBlocked;
  const canManageStatus = canEdit;
  const canApprove = Boolean(job && user && (user.user_type === 'ADMIN' || user.user_type === 'MODERATOR'));

  const experienceRangeMap: Record<string, string> = {
    "0_1": t('experienceRanges.zeroToOne'),
    "2_3": t('experienceRanges.twoToThree'),
    "4_6": t('experienceRanges.fourToSix'),
    "7_PLUS": t('experienceRanges.sevenPlus'),
  };

  const careerLabelMap: Record<string, string> = {
    MUSICOTERAPIA: t('careers.musicotherapy'),
    PSICOLOGIA: t('careers.psychology'),
    PROFESORADO_PSICOLOGIA: t('careers.psychologyTeaching'),
    TERAPIA_OCUPACIONAL: t('careers.occupationalTherapy'),
  };

  const careerOptions = [
    { value: t('careers.musicotherapy'), label: t('careers.musicotherapy') },
    { value: t('careers.psychology'), label: t('careers.psychology') },
    { value: t('careers.psychologyTeaching'), label: t('careers.psychologyTeaching') },
    { value: t('careers.occupationalTherapy'), label: t('careers.occupationalTherapy') },
  ];

  const handleDeactivateJob = async () => {
    if (!job) return;
    try {
      setUpdatingJobStatus(true);
      await api.deleteJob(job.id);
      setJob({ ...job, is_active: false });
    } catch (err) {
      alert(err instanceof Error ? err.message : t('postedJobs.deactivate'));
    } finally {
      setUpdatingJobStatus(false);
    }
  };

  const handleReactivateJob = async () => {
    if (!job) return;
    try {
      setUpdatingJobStatus(true);
      await api.reactivateJob(job.id);
      setJob({ ...job, is_active: true });
    } catch (err) {
      alert(err instanceof Error ? err.message : t('postedJobs.reactivate'));
    } finally {
      setUpdatingJobStatus(false);
    }
  };

  const handleApproveJob = async () => {
    if (!job) return;
    try {
      setApprovingJob(true);
      await api.approveJob(job.id);
      setJob({ ...job, is_approved: true });
    } catch (err) {
      alert(err instanceof Error ? err.message : t('approvals.approveJobError'));
    } finally {
      setApprovingJob(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (error || !job) {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
              {error}
            </Alert>
          )}
          <Title order={3}>{t('jobDetail.notFound')}</Title>
          <Button variant="outline" color="plum" onClick={() => navigate(-1)}>
            {t('jobDetail.goBack')}
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <>
      <Stack gap="md">
        <Card withBorder padding="xl" radius="lg" shadow="sm">
          <Stack gap="md">
            {applySuccessMessage && (
              <Alert
                color="green"
                title={t('applications.applySuccessTitle')}
                withCloseButton
                onClose={() => setApplySuccessMessage(null)}
              >
                {applySuccessMessage}
              </Alert>
            )}
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <div>
                {job.careers_affine && job.careers_affine.length > 0 && (
                  <Group gap="xs" wrap="wrap" mb={6}>
                    {job.careers_affine.map((career) => (
                      <Badge key={career} variant="outline" color="orange" radius="xl">
                        {careerLabelMap[career] || career}
                      </Badge>
                    ))}
                  </Group>
                )}
                <Title order={2}>{job.title}</Title>
                <Text c="dimmed">{job.company_name} • {job.location} {job.is_remote ? `• ${t('common.remote')}` : ''}</Text>
              </div>
              <Group gap="sm" wrap="wrap" align="center">
                {canApprove && job.is_approved === false && (
                  <Button
                    variant="light"
                    color="green"
                    onClick={handleApproveJob}
                    loading={approvingJob}
                    size="xs"
                    leftSection={<IconCheck size={14} />}
                  >
                    {t('approvals.approve')}
                  </Button>
                )}
                {canManageStatus && job.is_active && (
                  <Button
                    variant="light"
                    color="red"
                    onClick={handleDeactivateJob}
                    loading={updatingJobStatus}
                    size="xs"
                  >
                    {t('postedJobs.deactivate')}
                  </Button>
                )}
                {canManageStatus && job.is_active === false && (
                  <Button
                    variant="light"
                    onClick={handleReactivateJob}
                    loading={updatingJobStatus}
                    size="xs"
                  >
                    {t('postedJobs.reactivate')}
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="outline"
                    color="plum"
                    component={Link}
                    to={`/jobs/${job?.id}/edit`}
                    size="xs"
                  >
                    {t('common.editJob')}
                  </Button>
                )}
              </Group>
            </Group>
            <Group gap="xs" wrap="wrap">
              <Badge color="orange" variant="light">{job.job_type.replace('_', ' ')}</Badge>
              <Badge color="orange" variant="light">{experienceRangeMap[job.experience_range] || job.experience_range}</Badge>
              {!job.is_approved && (user?.user_type === 'ADMIN' || user?.user_type === 'MODERATOR') && (
                <Badge color="red" variant="light">{t('approvals.pendingApproval')}</Badge>
              )}
            </Group>
            {(job.requires_at_title
              || job.requires_at_diploma_uba
              || job.requires_graduated
              || job.requires_school_inclusion_course
              || job.requires_hr_courses
              || (job.careers_affine && job.careers_affine.length > 0)
              || job.requirements) && (
                <Stack gap="xs">
                  {job.requires_at_title && (
                    <Badge color="orange" variant="light">
                      {t('jobDetail.requiresAtCourse')}
                    </Badge>
                  )}
                  {job.requires_at_diploma_uba && (
                    <Badge color="orange" variant="light">
                      {t('jobDetail.requiresAtDiplomaUba')}
                    </Badge>
                  )}
                  {job.requires_graduated && (
                    <Badge color="orange" variant="light">
                      {t('jobDetail.requiresDegreeTitle')}
                    </Badge>
                  )}
                  {job.requires_school_inclusion_course && (
                    <Badge color="orange" variant="light">
                      {t('jobDetail.requiresSchoolInclusionCourse')}
                    </Badge>
                  )}
                  {job.requires_hr_courses && (
                    <Badge color="orange" variant="light">
                      {t('jobDetail.requiresHrCourses')}
                    </Badge>
                  )}
                  {job.requirements && (
                    <Group gap="xs" wrap="wrap">
                      <Text fw={600}>{t('jobDetail.otherSkills')}</Text>
                      <Text>{job.requirements}</Text>
                    </Group>
                  )}
                </Stack>
              )}
            {job.short_description && (
              <Stack gap={4}>
                <Text fw={600}>{t('createJob.fields.shortDescription')}</Text>
                <Text>{job.short_description}</Text>
              </Stack>
            )}
            <Stack gap={4}>
              <Text fw={600}>{t('createJob.fields.description')}</Text>
              <Text>{job.description}</Text>
            </Stack>
            {canViewApplicants() && (
              <>
                <Divider />
                <div>
                  <Group justify="space-between" align="center" mb="md" wrap="wrap">
                    <Title order={4}>{t('jobDetail.applicantsTitle', { count: applicants.length })}</Title>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => setShowApplicantsList((prev) => !prev)}
                    >
                      {showApplicantsList
                        ? t('jobDetail.hideApplicants')
                        : t('postedJobs.viewPostulations')}
                    </Button>
                  </Group>
                  {applicantsLoading ? (
                    <Center py="lg">
                      <Loader size="sm" />
                    </Center>
                  ) : applicants.length === 0 ? (
                    <Text c="dimmed" size="sm">{t('jobDetail.noApplicants')}</Text>
                  ) : (
                    <>
                      <Text c="dimmed" size="sm">
                        {t('jobDetail.applicantsTitle', { count: applicants.length })}
                      </Text>
                      {showApplicantsList && (
                        <Stack gap="xs" mt="sm">
                          {applicants.map((app) => (
                            <Group key={app.id} justify="space-between" align="center" wrap="wrap">
                              <Text size="sm">{app.applicant_username}</Text>
                              {app.resume_url && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  component="a"
                                  href={app.resume_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('jobDetail.download')}
                                </Button>
                              )}
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </Stack>
        </Card>
        <Group gap="sm" wrap="wrap" align="flex-start">
          {canApply && (
            <Button
              onClick={() => setApplyModalOpen(true)}
              loading={applying}
            >
              {hasWithdrawn ? t('applications.reapply') : t('common.applyNow')}
            </Button>
          )}
          {reapplyBlocked && (
            <Text c="dimmed" size="sm">
              {t('applications.reapplyBlocked')}
            </Text>
          )}
        </Group>
        <Group gap="sm" justify="center" wrap="wrap">
          <Button variant="outline" color="plum" onClick={() => navigate(-1)}>
            {t('jobDetail.goBack')}
          </Button>
        </Group>
      </Stack>

      <Modal
        opened={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        title={t('jobDetail.applyToJob')}
        size="md"
      >
        <Stack gap="md">
          <Autocomplete
            label={t('applications.careerLabel')}
            value={career}
            onChange={setCareer}
            placeholder={t('applications.careerPlaceholder')}
            data={careerOptions.map((option) => option.label)}
            clearable
          />
          <Text size="xs" c="dimmed">
            {t('applications.careerHelp')}
          </Text>
          <Select
            label={t('applications.progressLabel')}
            value={careerProgress}
            onChange={setCareerProgress}
            placeholder={t('applications.progressPlaceholder')}
            data={[
              { value: 'INITIAL', label: t('applications.progress.initial') },
              { value: 'INTERMEDIATE', label: t('applications.progress.intermediate') },
              { value: 'ADVANCED', label: t('applications.progress.advanced') },
            ]}
            clearable
          />
          <FileInput
            label={t('jobDetail.resumeLabel')}
            placeholder={t('jobDetail.resumePlaceholder')}
            accept="application/pdf"
            value={resume}
            onChange={setResume}
            leftSection={<IconUpload size={16} />}
            required
          />
          <Textarea
            label={t('jobDetail.coverLetterLabel')}
            placeholder={t('jobDetail.coverLetterPlaceholder')}
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
            minRows={4}
          />
          <Group justify="flex-end" gap="sm" wrap="wrap">
            <Button variant="subtle" onClick={() => setApplyModalOpen(false)}>
              {t('jobDetail.cancel')}
            </Button>
            <Button
              onClick={handleApply}
              loading={applying}
              disabled={!resume}
            >
              {t('jobDetail.submitApplication')}
            </Button>
          </Group>
        </Stack>
      </Modal>

    </>
  );
};

export default JobDetailPage;
